import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, ShieldCheck, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '@/admin/lib/i18n'
import api from '@/admin/lib/api'
import {
  headlessLogin,
  isAuthenticated,
  isMfaPending,
  isSessionConflict,
  authenticate2fa,
  type AllauthResponse,
} from '@/shared/lib/auth-api'
import { useSession } from '@/admin/hooks/use-session'
import { Logo } from '@/admin/components/ui/logo'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { buildApiUrl } from '@/shared/lib/api-origin'
import { getTenantSlugFromHost } from '@/shared/lib/tenant-host'
import { toastApiError } from '@/shared/lib/api-error'
import { applyTenantTheme, brandingToTheme } from '@/shared/lib/theme'
type LoginValues = { email: string; password: string }
type MfaValues = { code: string }

// Dev-only quick-login accounts - keep these aligned with the canonical local
// dev tenants, even while some seed paths still need cleanup.
//
// FOLLOW-UP: this list should be sourced from the backend (e.g.
// Tenant.is_dev_demo_account flag) so newly provisioned tenants appear
// automatically without a frontend edit. See docs/vanilla-tenant-audit-findings.md.
const DEV_ACCOUNTS = [
  { label: '12 Assured', email: 'developers+12-assured-admin@iwarranty.co', password: '12assured1234' },
  { label: 'Supagard Admin', email: 'developers+supagard-admin@iwarranty.co', password: 'supagard1234' },
  { label: 'UltraShield Admin', email: 'developers+ultrashield-admin@iwarranty.co', password: 'ultrashield1234' },
  { label: 'Owens Corning', email: 'developers+oc-admin@iwarranty.co', password: 'owenscorning1234' },
  { label: 'Demo Admin', email: 'developers+demo-admin@iwarranty.co', password: 'admin123!' },
]

interface TenantBranding {
  name: string
  slug: string
  logo_url: string | null
  primary_colour: string
  admin_branding: 'GENERIC' | 'COBRANDED' | 'BRANDED' | 'DISABLED'
  theme?: Record<string, string>
}

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { tenantSlug: routeTenantSlug } = useParams<{ tenantSlug: string }>()
  // The tenant subdomain (`<slug>.portal.<env>`) is authoritative; the
  // `/:tenantSlug/login` path param is the generic-host fallback.
  const tenantSlug = getTenantSlugFromHost() || routeTenantSlug
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [branding, setBranding] = useState<TenantBranding | null>(null)
  const [ssoProvider, setSsoProvider] = useState<{ sso_enabled: boolean; provider_name: string | null } | null>(null)
  const [step, setStep] = useState<'credentials' | 'mfa'>('credentials')
  const { data: session, isLoading: sessionLoading } = useSession()

  const prefillEmail = searchParams.get('email') || ''
  const nextUrl = searchParams.get('next') || ''

  // A live session means the user is already signed in - visiting /login should
  // return them to where they were headed (or their default landing), never
  // strand them on the form. Skip while a second-factor prompt is in flight, so
  // the pending-MFA token isn't mistaken for a completed login.
  useEffect(() => {
    if (sessionLoading || step === 'mfa') return
    if (!session?.is_authenticated) return
    if (nextUrl) {
      navigate(nextUrl, { replace: true })
    } else if (session.user?.is_staff) {
      navigate('/staff/dashboard', { replace: true })
    } else {
      navigate('/dashboard', { replace: true })
    }
  }, [sessionLoading, session, nextUrl, step, navigate])

  // Shared post-authentication landing, reached after either a single-factor
  // login or completing the two-factor stage.
  async function finishLogin() {
    if (tenantSlug) {
      sessionStorage.setItem('tenantSlug', tenantSlug)
    } else {
      sessionStorage.removeItem('tenantSlug')
    }
    await queryClient.invalidateQueries({ queryKey: ['session'] })
    void i18n.reloadResources()
    toast.success('Signed in')
    // allauth's user payload omits is_staff; read it from our token-authed
    // session probe to pick the right landing page.
    const session = await api.get('/auth/session/').then((r) => r.data)
    if (nextUrl) {
      navigate(nextUrl)
    } else if (session?.user?.is_staff) {
      navigate('/staff/dashboard')
    } else {
      navigate('/dashboard')
    }
  }

  const emailLabel = t('common.email')
  const emailPlaceholder = t('login.emailPlaceholder')
  const passwordLabel = t('common.password')

  const fields = useMemo<FormField[]>(() => [
    {
      type: 'email',
      name: 'email',
      label: emailLabel,
      placeholder: emailPlaceholder,
      value: prefillEmail,
      autoComplete: 'email',
      required: 'Enter a valid email',
    },
    {
      type: 'password',
      name: 'password',
      label: passwordLabel,
      placeholder: '••••••••',
      value: '',
      autoComplete: 'current-password',
      required: 'Password is required',
    },
  ], [emailLabel, emailPlaceholder, passwordLabel, prefillEmail])
  const { Form, ref } = useForm(
    {
      name: 'admin-login-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as LoginValues
      setLoading(true)
      try {
        const result: AllauthResponse = await headlessLogin(
          values.email,
          values.password,
          rememberMe,
        )
        if (isAuthenticated(result)) {
          await finishLogin()
        } else if (isMfaPending(result)) {
          // Credentials were valid; the session token for the pending stage is
          // already stored. Prompt for the second factor.
          setStep('mfa')
        } else if (isSessionConflict(result)) {
          // headlessLogin already tried to heal a 409 transparently (tear down
          // the token + cookie session, retry). A 409 still here is a genuinely
          // unrecoverable conflict - say so honestly rather than the misleading
          // "invalid password" the generic branch would show on an empty body.
          toast.error(
            result.errors?.[0]?.message ??
              t('login.sessionConflict', {
                defaultValue:
                  'Another session is still active in this browser. Refresh the page and sign in again.',
              }),
          )
        } else {
          toast.error(result.errors?.[0]?.message ?? 'Invalid email or password')
        }
      } catch (err) {
        toastApiError(err, 'Invalid email or password');
      } finally {
        setLoading(false)
      }
    },
    [tenantSlug, nextUrl, prefillEmail, rememberMe],
  )

  const mfaFields = useMemo<FormField[]>(() => [
    {
      type: 'text',
      name: 'code',
      label: t('staffLogin.codeLabel'),
      placeholder: t('staffLogin.codePlaceholder'),
      value: '',
      autoFocus: true,
      autoComplete: 'one-time-code',
      inputMode: 'numeric',
      maxLength: 6,
      required: 'Enter your 6-digit code',
    },
  ], [t])
  const { Form: MfaForm } = useForm(
    {
      name: 'admin-mfa-form',
      submit: 'footer',
      fields: mfaFields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as MfaValues
      setLoading(true)
      try {
        const result = await authenticate2fa(values.code)
        if (isAuthenticated(result)) {
          await finishLogin()
        } else {
          toast.error(result.errors?.[0]?.message ?? 'Invalid verification code')
        }
      } catch (err) {
        toastApiError(err, 'Invalid verification code');
      } finally {
        setLoading(false)
      }
    },
    [mfaFields],
  )

  // Fetch tenant branding when slug is present
  useEffect(() => {
    if (!tenantSlug) return
    let cancelled = false
    api
      .get(`/public/tenants/${tenantSlug}/branding/`)
      .then((res) => {
        if (cancelled) return
        setBranding(res.data)
        // First-view page: apply the tenant's authored theme (if any) so login
        // reflects it before auth. Un-themed tenants inherit the platform theme.
        applyTenantTheme(brandingToTheme({ theme: res.data?.theme }))
      })
      .catch(() => {
        // Tenant not found - ignore, show default branding
      })
    return () => {
      cancelled = true
    }
  }, [tenantSlug])

  // Check if SSO is available for this tenant
  useEffect(() => {
    if (!tenantSlug) return
    let cancelled = false
    api
      .get(`/auth/sso/status/${tenantSlug}/`)
      .then((res) => {
        if (!cancelled) setSsoProvider(res.data)
      })
      .catch(() => {
        // SSO status check failed - don't show SSO button
      })
    return () => {
      cancelled = true
    }
  }, [tenantSlug])

  function handleSSOLogin() {
    if (!tenantSlug) return
    // Thread the return-to path through the SAML round-trip so the user
    // lands back on the original page after authentication, not the dashboard.
    const base = buildApiUrl(`/accounts/saml/${tenantSlug}/login/`)
    const url = nextUrl ? `${base}?return_to=${encodeURIComponent(nextUrl)}` : base
    window.location.href = url
  }

  async function quickLogin(email: string, password: string) {
    const form = ref.current
    if (!form) return

    await form.setFieldValue('email', email, false)
    await form.setFieldValue('password', password, false)
    await form.submitForm()
  }

  const mode = branding?.admin_branding ?? 'GENERIC'
  const [logoError, setLogoError] = useState(false)
  const isBranded = (mode === 'COBRANDED' || mode === 'BRANDED') && branding?.logo_url && !logoError

  const renderOrgLogo = (height = 'h-8', maxWidth = 'max-w-[200px]') => {
    if (!branding?.logo_url) return null
    return (
      <img
        src={branding.logo_url}
        alt={branding.name}
        className={`${height} ${maxWidth} object-contain`}
        onError={() => setLogoError(true)}
      />
    )
  }

  // Determine which logo to show on the right panel
  const renderBrandLogo = () => {
    if (isBranded) return renderOrgLogo()
    return <Logo size="md" />
  }

  const renderMobileLogo = () => {
    if (isBranded) {
      return (
        <div className="flex flex-col gap-3">
          {renderOrgLogo('h-7', 'max-w-[160px]')}
          {mode === 'COBRANDED' && (
            <div className="flex items-center gap-2 text-[10px] text-[var(--ink-3)]">
              <span>Powered by</span>
              <Logo size="sm" />
            </div>
          )}
        </div>
      )
    }
    return <Logo size="lg" />
  }

  // Hold the form back until the session probe settles, so an authenticated
  // visitor is redirected (above) rather than flashing the login form.
  if (step !== 'mfa' && (sessionLoading || session?.is_authenticated)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[var(--bg)]">
      {/* ── Left: Premium brand panel ────────────────────── */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between bg-gradient-to-br from-[var(--brand)] via-[color-mix(in_oklab,var(--brand),white_12%)] to-[color-mix(in_oklab,var(--brand),white_22%)] p-12 relative overflow-hidden">
        {/* Subtle dot pattern overlay */}
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] bg-[length:24px_24px] opacity-[0.04]"
        />
        <div className="relative z-10">
          <div className="mb-20">
            {mode === 'BRANDED' && branding?.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.name}
                className="h-8 max-w-[200px] object-contain"
              />
            ) : (
              <Logo size="lg" variant="white" />
            )}
          </div>

          <h1 className="text-[42px] leading-[1.15] tracking-[-0.01em] font-display text-[#faf9f7]">
            {t('login.tagline')}
          </h1>
          <p className="mt-3 max-w-sm text-base leading-relaxed text-[var(--bg)]">
            {t('login.description')}
          </p>
        </div>

        <div className="relative z-10 flex gap-5 text-[11px] font-normal uppercase tracking-[0.2em] text-[var(--ink-3)]">
          <span>SOC 2 Compliant</span>
          <span>&middot;</span>
          <span>Multi-Tenant</span>
          <span>&middot;</span>
          <span>Real-time Analytics</span>
        </div>
      </div>
      {/* ── Right: Login Form ────────────────────────── */}
      <div className="flex flex-1 items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-[360px]">
          {/* Mobile logo */}
          <div className="mb-10 lg:hidden">{renderMobileLogo()}</div>

          <div className="mb-8">
            {/* Desktop logo */}
            <div className="hidden lg:block mb-6">{renderBrandLogo()}</div>

            <h2 className="text-[36px] leading-[1.1] font-display text-[var(--ink)]">
              {step === 'mfa' ? t('staffLogin.verification') : t('login.signIn')}
            </h2>
            <p className="clara-small mt-1">
              {step === 'mfa' ? t('staffLogin.mfaDescription') : t('login.signInDescription')}
            </p>
          </div>

          {step === 'mfa' ? (
            <>
              <MfaForm className="space-y-4" />
              <Button
                type="submit"
                form="admin-mfa-form"
                disabled={loading}
                className="mt-4 w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)] disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    {t('staffLogin.verifyButton')}
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="link"
                onClick={() => setStep('credentials')}
                className="w-full cursor-pointer py-2 text-[11px] text-[var(--ink-3)]"
              >
                {t('staffLogin.backToLogin')}
              </Button>
            </>
          ) : (
          <>
          <Form className="space-y-4" />

          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(!!checked)}
                  className="h-4 w-4 rounded-[2px] border accent-[var(--green)]"
                />
                <span className="text-[11px] text-[var(--ink-3)]">
                  {t('login.keepLoggedIn', { defaultValue: 'Keep me logged in' })}
                </span>
              </Label>

              <a
                href={`/forgot-password${tenantSlug ? `?tenant=${tenantSlug}` : ''}`}
                className="text-[11px] text-[var(--green)] transition-colors"
              >
                Forgot password?
              </a>
            </div>
          </div>

          <Button
            type="submit"
            form="admin-login-form"
            disabled={loading}
            className="mt-4 w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)] disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t('login.signInButton')}
          </Button>

          {/* SSO button - shown when tenant has SAML SSO configured */}
          {ssoProvider?.sso_enabled && (
            <div className="mt-5">
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-[var(--rule)]" />
                <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">or</span>
                <div className="flex-1 h-px bg-[var(--rule)]" />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleSSOLogin}
                className="w-full cursor-pointer rounded-[4px] py-2.5 text-[11px] uppercase tracking-[0.1em] border-[0.5px] border-[var(--rule-strong)] text-[var(--ink)] transition-all duration-150 hover:bg-[var(--green-bg)] hover:text-[var(--green)] hover:border-[var(--green-border)]"
              >
                <ShieldCheck className="mr-2 h-4 w-4" />
                {ssoProvider.provider_name
                  ? `Sign in with ${ssoProvider.provider_name}`
                  : 'Sign in with SSO'}
              </Button>
            </div>
          )}

          {/* Dev quick-links */}
          {import.meta.env.DEV && (
            <div className="mt-10 border-t-[0.5px] border-t-[var(--rule)] pt-6">
              <p className="clara-label mb-3">{t('login.devAccounts')}</p>
              <div className="flex flex-wrap gap-2">
                {DEV_ACCOUNTS.map((a) => (
                  <Button
                    key={a.email}
                    type="button"
                    variant="outline"
                    onClick={() => void quickLogin(a.email, a.password)}
                    className="px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] rounded-full transition-all duration-150 cursor-pointer bg-[var(--bg-subtle)] text-[var(--ink-3)] border-[var(--rule)] hover:bg-[var(--green-bg)] hover:text-[var(--green)] hover:border-[var(--green-border)]"
                  >
                    {a.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
